import { Calendar } from 'lucide-react';
import { palette, shadow, admin } from '../../styles/tokens';
import { EmptyState } from '../../components/common/EmptyState';
import { ScheduleSkeleton } from '../../components/common/Skeleton';
import { WeekView } from '../../components/schedule/WeekView';
import { DayView } from '../../components/schedule/DayView';
import { MonthView } from '../../components/schedule/MonthView';
import { DayCanvas } from '../../components/schedule/DayCanvas';
import { DayCanvasWithRouteToggle } from '../../components/schedule/DayCanvasWithRouteToggle';
import { WeekCanvasContainer } from '../../components/schedule/WeekCanvasContainer';
import { MonthCanvasContainer } from '../../components/schedule/MonthCanvasContainer';
import { TeamCanvas } from '../../components/schedule/TeamCanvas';
import { YearCanvasContainer } from '../../components/schedule/YearCanvasContainer';
import { resolveDayLayout } from '../../lib/scheduleLayout';
import type { ScheduleEntry } from '../../types';
import type { ScheduleUser } from '../../components/schedule/scheduleHelpers';

type ViewMode = 'day' | 'week' | 'month' | 'team' | 'year';

interface DayCanvasDispatcherLike {
  handleReassign: (droppedEntryId: string, newResourceId: string) => void | Promise<void>;
  serverConflicts: unknown;
  conflictRpcName: string | undefined;
  handleOverride: () => Promise<void>;
  handleDismiss: () => void;
}

interface ScheduleMainCanvasProps {
  loading: boolean;
  error: string | null;
  entries: ScheduleEntry[];
  viewMode: ViewMode;
  isAppointmentCluster: boolean;
  emptyMessage: string;
  primaryAction: string;
  onCreate: () => void;
  effectiveAnchor: Date;
  bizHours: { open_time: string; close_time: string };
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  refresh: () => void;
  onToast: (toast: { msg: string; type: 'success' | 'error' }) => void;
  users: ScheduleUser[];
  onEntryTap: (entry: ScheduleEntry) => void;
  onEntryEdit: (entry: ScheduleEntry | null) => void;
  business: { id?: string; industry?: string | null; tier?: string | null } | null;
  /** The business's 17-category id — threaded down for the cluster resolver. */
  categoryId?: string | null;
  dayCanvasFeatureFlags: unknown;
  businessTier: string | null;
  lens: 'calendar' | 'crew' | 'route';
  setLensInUrl: (lens: 'calendar' | 'crew' | 'route') => void;
  routeLensEnabled: boolean;
  dayCanvasTravelBuffer: number;
  dayCanvasDispatcher: DayCanvasDispatcherLike;
  setAnchorDate: (next: Date) => void;
  setDate: (dateIso: string) => void;
  setZoom: (zoom: 'day' | 'week' | 'month' | 'year' | 'today') => void;
  handleDayClick: (date: Date) => void;
  onYearDateSelect: (dateIso: string) => void;
}

export function ScheduleMainCanvas({
  loading,
  error,
  entries,
  viewMode,
  isAppointmentCluster,
  emptyMessage,
  primaryAction,
  onCreate,
  effectiveAnchor,
  bizHours,
  onPrev,
  onNext,
  onToday,
  refresh,
  onToast,
  users,
  onEntryTap,
  onEntryEdit,
  business,
  categoryId,
  dayCanvasFeatureFlags,
  businessTier,
  lens,
  setLensInUrl,
  routeLensEnabled,
  dayCanvasTravelBuffer,
  dayCanvasDispatcher,
  setAnchorDate,
  setDate,
  setZoom,
  handleDayClick,
  onYearDateSelect,
}: ScheduleMainCanvasProps) {
  return (
    <div style={{ background: palette.white, borderRadius: admin.cardRadius, border: `1px solid ${admin.border}`, overflow: 'hidden', boxShadow: shadow.card }}>
      {loading ? <div style={{ padding: '12px' }}><ScheduleSkeleton /></div>
      : error ? <div style={{ padding: '40px 0', textAlign: 'center', color: palette.red, fontSize: 13.5 }}>{error}</div>
      : entries.length === 0 && viewMode !== 'year' && !(isAppointmentCluster && viewMode === 'day') ? (
        <EmptyState
          icon={<Calendar size={22} />}
          title={emptyMessage}
          subtitle="Drag from the unassigned bar or create a new entry."
          actionLabel={primaryAction}
          onAction={onCreate}
        />
      ) : <>
          {viewMode === 'week' && isAppointmentCluster && (
            <WeekCanvasContainer
              effectiveAnchor={effectiveAnchor}
              entries={entries}
              businessOpen={bizHours.open_time}
              businessClose={bizHours.close_time}
              onEntryTap={(entryId) => {
                const entry = entries.find((e) => e.id === entryId);
                if (entry) onEntryTap(entry);
              }}
              onPrevWeek={onPrev}
              onNextWeek={onNext}
              onToday={onToday}
              refresh={refresh}
              onToast={onToast}
            />
          )}
          {viewMode === 'week' && !isAppointmentCluster && <WeekView weekStart={effectiveAnchor} entries={entries} users={users} onEntryClick={onEntryEdit} />}
          {viewMode === 'day' && isAppointmentCluster && (() => {
            const dayDateIso = effectiveAnchor.toISOString().split('T')[0];
            const dayCanvasOperators = users.map((u) => ({ id: u.id, display_name: `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || 'Operator' }));
            const dayCanvasLayoutMode = resolveDayLayout({ featureFlags: dayCanvasFeatureFlags as never, businessTier, operatorCount: dayCanvasOperators.length });
            const isRouteMode = lens === 'route';
            return (
              <DayCanvasWithRouteToggle businessId={business?.id} effectiveAnchor={effectiveAnchor} initialRouteVisible={isRouteMode}
                onRouteVisibilityChange={(visible) => setLensInUrl(visible ? 'route' : 'calendar')}
                onStopTap={(entryId) => { const entry = entries.find((e) => e.id === entryId); if (entry) onEntryTap(entry); }}>
                <DayCanvas date={dayDateIso} industry={business?.industry ?? undefined} routeLensEnabled={routeLensEnabled} travelBufferMinutes={dayCanvasTravelBuffer}
                  layoutMode={dayCanvasLayoutMode} entries={entries} operators={dayCanvasOperators}
                  onEntryTap={(entryId) => { const entry = entries.find((e) => e.id === entryId); if (entry) onEntryTap(entry); }}
                  onResourceDrop={dayCanvasDispatcher.handleReassign} conflicts={dayCanvasDispatcher.serverConflicts as never} conflictRpcName={dayCanvasDispatcher.conflictRpcName}
                  onConflictOverride={dayCanvasDispatcher.handleOverride} onConflictDismiss={dayCanvasDispatcher.handleDismiss}
                  dayStartHour={parseInt(bizHours.open_time.split(':')[0] ?? '7', 10)} dayEndHour={parseInt(bizHours.close_time.split(':')[0] ?? '19', 10)}
                  onPrevDay={onPrev}
                  onNextDay={onNext}
                  onToday={onToday} />
              </DayCanvasWithRouteToggle>
            );
          })()}
          {viewMode === 'day' && !isAppointmentCluster && <DayView date={effectiveAnchor} entries={entries} users={users} onEntryClick={onEntryEdit} />}
          {viewMode === 'month' && isAppointmentCluster && (
            <MonthCanvasContainer effectiveAnchor={effectiveAnchor} entries={entries} business={business} categoryId={categoryId} refresh={refresh} onToast={onToast}
              onCellTap={(date) => { const d = new Date(`${date}T12:00:00`); setAnchorDate(d); setDate(date); setZoom('day'); }}
              onPrevMonth={onPrev}
              onNextMonth={onNext}
              onToday={onToday} />
          )}
          {viewMode === 'month' && !isAppointmentCluster && <MonthView year={effectiveAnchor.getFullYear()} month={effectiveAnchor.getMonth()} entries={entries} onDayClick={handleDayClick} />}
          {viewMode === 'year' && (
            <YearCanvasContainer
              businessId={business?.id}
              year={effectiveAnchor.getFullYear()}
              onToast={onToast}
              onSelectMonth={(monthIndex) => {
                const next = new Date(effectiveAnchor.getFullYear(), monthIndex, 1);
                setAnchorDate(next);
                setDate(next.toISOString().split('T')[0] ?? '');
                setZoom('month');
              }}
              onSelectDate={onYearDateSelect}
            />
          )}
          {viewMode === 'team' && (() => {
            const tier = business?.tier ?? null;
            const tierAllowed = tier === 'business' || tier === 'trial';
            if (!tierAllowed) {
              return (
                <EmptyState
                  icon={<Calendar size={22} />}
                  title="Team scoreboard requires the Business plan"
                  subtitle="Track operator workload, reassign work, and optimize schedules."
                  actionLabel="View plans"
                  onAction={() => { window.location.href = '/settings/plans'; }}
                />
              );
            }
            const weekStartDate = new Date(effectiveAnchor);
            const dow = weekStartDate.getDay();
            weekStartDate.setDate(weekStartDate.getDate() - (dow === 0 ? 6 : dow - 1));
            const weekEndDate = new Date(weekStartDate);
            weekEndDate.setDate(weekStartDate.getDate() + 6);
            return (
              <TeamCanvas
                businessId={business?.id ?? ''}
                weekStart={weekStartDate}
                weekEnd={weekEndDate}
                resources={users}
                onEntryClick={onEntryEdit}
                onPrevWeek={onPrev}
                onNextWeek={onNext}
                onToday={onToday}
              />
            );
          })()}
        </>
      }
    </div>
  );
}
